import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminNav from '../components/admin/AdminNav';
import '../styles/pages/AdminPricingRulesPage.scss';

const API_URL = process.env.REACT_APP_API_URL;

// Lista de proveedores conocidos para pre-poblar el formulario.
// Puedes expandir esta lista si agregas más proveedores en el futuro.
const KNOWN_PROVIDERS = ['Marpico', 'PromoOpcion', 'CDO', 'Boton Promo', 'Shopify'];

const AdminPricingRulesPage = () => {
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const token = localStorage.getItem('admToken');
        const response = await axios.get(`${API_URL}/admin/pricing-rules`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Convertir el array de reglas a un objeto para fácil acceso por providerName
        const rulesObject = response.data.reduce((acc, rule) => {
          // Al cargar, multiplicamos por 100 para mostrar el porcentaje como un número entero/decimal (ej: 25 en lugar de 0.25)
          acc[rule.providerName] = { ...rule, percentage: rule.percentage * 100 };
          return acc;
        }, {});

        // Asegurarse de que todos los proveedores conocidos tengan una entrada en la UI,
        // incluso si aún no tienen una regla en la base de datos.
        KNOWN_PROVIDERS.forEach(provider => {
          if (!rulesObject[provider]) {
            rulesObject[provider] = {
              providerName: provider,
              operation: 'add',
              percentage: 0, // El valor inicial ya está en formato de visualización (0, no 0.00)
              isEnabled: false,
            };
          }
        });
        
        setRules(rulesObject);
      } catch (err) {
        setError('Error al cargar las reglas de precios.');
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  const handleInputChange = (providerName, field, e) => {
    // Para el checkbox, el valor es 'checked'. Para otros inputs, es 'value'.
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    
    // Actualizamos el estado con el valor del input directamente, sin conversiones
    setRules(prevRules => ({
      ...prevRules,
      [providerName]: {
        ...prevRules[providerName],
        [field]: value,
      },
    }));
  };

  const handleSaveRule = async (providerName) => {
    const ruleToSave = rules[providerName];
    // Justo antes de enviar, convertimos el porcentaje de visualización (ej: "25")
    // al formato que el backend espera (ej: 0.25).
    const percentageAsFloat = parseFloat(ruleToSave.percentage) / 100;

    if (isNaN(percentageAsFloat)) {
        setError(`El porcentaje para ${providerName} no es un número válido.`);
        return;
    }

    const payload = {
      ...ruleToSave,
      percentage: percentageAsFloat,
    };

    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('admToken');
      await axios.post(`${API_URL}/admin/pricing-rules`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(`Regla para "${providerName}" guardada exitosamente.`);
      setTimeout(() => setSuccess(''), 3000); // Limpiar mensaje de éxito después de 3 segundos
    } catch (err) {
      setError(`Error al guardar la regla para ${providerName}.`);
    }
  };

  if (loading) return <div className="container">Cargando reglas...</div>;

  return (
    <div className="admin-pricing-rules-page container">
      <header className="admin-pricing-rules-page__header">
        <h1>Reglas de precios</h1>
      </header>

      <AdminNav />

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="rules-container">
        {Object.keys(rules).sort().map(providerName => { // .sort() para ordenar alfabéticamente
          const rule = rules[providerName];
          return (
            <div key={providerName} className="rule-card">
              <h3>{providerName}</h3>
              <div className="form-group">
                <label>Operación</label>
                <select 
                  value={rule.operation} 
                  onChange={(e) => handleInputChange(providerName, 'operation', e)}
                >
                  <option value="add">Sumar al precio</option>
                  <option value="subtract">Restar del precio</option>
                </select>
              </div>
              <div className="form-group">
                <label>Porcentaje (%)</label>
                <input 
                  type="number" 
                  step="0.01"
                  // El valor ahora es directamente el del estado, que ya está en formato de visualización
                  value={rule.percentage} 
                  onChange={(e) => handleInputChange(providerName, 'percentage', e)} 
                />
              </div>
              <div className="form-group form-check">
                <input 
                  type="checkbox"
                  id={`enabled-${providerName}`}
                  checked={rule.isEnabled}
                  onChange={(e) => handleInputChange(providerName, 'isEnabled', e)}
                />
                <label htmlFor={`enabled-${providerName}`}>Habilitada</label>
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => handleSaveRule(providerName)}
              >
                Guardar Regla
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminPricingRulesPage;